import React, { useEffect, useRef, useState } from 'react';
import { CommonBackContent } from '../Layouts/CommonBackContent';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import { DownloadStoriesPreviewModel } from '@/types/home';
import { InsHashTagSearchModel } from '@/types/anonymously.interface';
import { useUserProvider } from '@/context/UserProvider';
import { searchInsHashtagListData } from '@/lib/service/anonymously';
import { message } from 'antd';
import CustomersTalkUsContent from '../LandingPage/CustomersTalkUsContent';

const FAQContent = dynamic(() => import('../LandingPage/FAQContent'), { ssr: true });
const DownloadInstagramContent = dynamic(() => import('../LandingPage/DownloadInstagramContent'), { ssr: true });
const TopSearchContent = dynamic(() => import('../LandingPage/TopSearch'), { ssr: true });
const ChooseContent = dynamic(() => import('../LandingPage/ChooseContent'), { ssr: true });
const GeneralExampDownloadContent = dynamic(() => import('../LandingPage/GeneralExampDownloadContent'), { ssr: true });
const WhyDownloadStoriesContent = dynamic(() => import('../LandingPage/WhyDownloadStoriesContent'), { ssr: true });
const InstagramActivityTrackerContent = dynamic(() => import('../LandingPage/InstagramActivityTracker'), { ssr: true });
const BestTipsContent = dynamic(() => import('../LandingPage/BestTipsContent'), { ssr: true });
const DownloadStoriesBottomContent = dynamic(() => import('../LandingPage/DownloadStoriesBottomContent'), {
  ssr: true,
});
const HashtagListComponent = dynamic(() => import('./hashtag-list'), { ssr: false });

const InstagramHashtagGeneratorSub = ({ data }: { data?: any }) => {
  const [contentData, setContentData] = useState<DownloadStoriesPreviewModel>(data?.contentData);
  const resultRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [searchStr, setSearchStr] = useState('');
  const [inputVisible, setInputVisible] = useState(true);
  const [queryListModel, setQueryListModel] = useState<InsHashTagSearchModel[]>([]);
  const { onOpenLoginModal, isLogin, onChangedLoginView } = useUserProvider();

  useEffect(() => {
    setContentData(data?.contentData);
  }, [data]);

  const onValueChanged = (e?: string) => {
    setSearchStr(e ?? '');
  };

  const onSearchClick = (e?: string) => {
    if (!isLogin) {
      onOpenLoginModal(true);
      onChangedLoginView(false);
      return;
    }

    if (e?.length > 0) {
      if (loading) {
        return;
      }
      setLoading(true);
      if (e !== searchStr) {
        setQueryListModel([]);
        setSearchStr(e);
      }

      searchInsHashtagListData(e, { isPremium: false, bizFuncName: 'hash_tag' })
        .then((res) => {
          if (res?.code === 0) {
            setQueryListModel(res?.data ?? []);
            srollToResultView();
          } else {
            setQueryListModel([]);
            message.error(res?.message);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const srollToResultView = () => {
    if (resultRef?.current) {
      const targetTop = resultRef.current.offsetTop - 220;

      window.scrollTo({
        top: targetTop,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      <CommonBackContent style={{ minHeight: '100vh' }}>
        <TopSearchContent
          inputPlaceholder={t('Example: love, food, travel, photography')}
          btnPlaceholder={t('Generate Now')}
          topInfo={contentData?.contentInfo?.hero}
          loading={loading}
          searchVal={searchStr}
          onValueChanged={onValueChanged}
          onInputVisibleChanged={(visible) => setInputVisible(visible)}
          onSearchClick={onSearchClick}
        />

        <div ref={resultRef}>
          <HashtagListComponent hashtags={queryListModel} />
        </div>

        <DownloadInstagramContent data={contentData?.contentInfo['section-1']} />
        <ChooseContent data={contentData?.contentInfo['section-2']} />
        <GeneralExampDownloadContent data={contentData?.contentInfo['section-3']} />
        <WhyDownloadStoriesContent data={contentData?.contentInfo['section-4']} />
        <InstagramActivityTrackerContent
          contentData={contentData}
          isTracker={true}
          webBg={'https://res-front.pumpsoul.com/ins-prod/image/activity_tracker_bg_web_0110_1.webp'}
          phoneBg={'https://res-front.pumpsoul.com/ins-prod/image/activity_tracker_bg_phone_0109.webp'}
        />
        <CustomersTalkUsContent contentInfo={contentData?.contentInfo?.['section-user-reviews']} />
        {contentData?.faqList && (
          <FAQContent faqInfo={contentData?.contentInfo?.['section-faq']} faqList={contentData?.faqList} />
        )}
        <BestTipsContent
          title={contentData?.contentInfo?.['section-top-blog']?.title}
          blogList={contentData?.topBlogList}
        />
        <BestTipsContent title={contentData?.contentInfo?.['section-blog']?.title} blogList={contentData?.blogList} />
      </CommonBackContent>

      <DownloadStoriesBottomContent
        bottomInfo={contentData?.contentInfo['section-buttom']}
        placeholder={t('Instagram Username (@name)')}
        buttonTitle={t('Reveal Now')}
        loading={loading}
        searchVal={searchStr}
        inputVisible={inputVisible}
        onValueChanged={onValueChanged}
        onSearchClick={(e) => {
          onSearchClick(e);
        }}
      />
    </>
  );
};

export default InstagramHashtagGeneratorSub;
